import {
  DataTable,
  selectColumn,
  createSortableColumn,
  baseScheduleQueryKeys,
  BaseSchedule,
  createCustomColumn,
  Badge,
  Button,
  Group,
  MultiLevelDropdownMenu,
} from "toptaal-shared"
import {
  createFileRoute,
  useNavigate,
  useRouter,
  useSearch,
} from "@tanstack/react-router"
import ScreenWrapper from "@/components/ScreenWrapper"
import { TABLE_PAGE_SIZE } from "toptaal-shared"
import { checkPermissions } from "@/utils/checkPermissions"
import {
  usePaginatedTableWithUrlState,
  createTableSearchSchema,
  useExportMutation,
} from "toptaal-shared"
import type { ColumnDef } from "@tanstack/react-table"
import z from "zod"
import { TableHeaderActions } from "@/components/data-table/TableHeaderActions"
import {
  BASE_SCHEDULE_STORAGE_KEY,
  useBaseScheduleFilters,
} from "@/hooks/filters/useBaseScheduleFilters"

export const Route = createFileRoute("/beheer/roosters/")({
  component: RouteComponent,
  beforeLoad: () => checkPermissions("read", "planning"),
  validateSearch: createTableSearchSchema().extend({
    finalized: z.boolean().optional().default(false),
  }),
})

const ALL_COLUMN_IDS = [
  "id",
  "group",
  "teachersLabel",
  "numberOfLessonsRequired",
  "status",
]

function RouteComponent() {
  const { finalized, ...searchParams } = useSearch({
    from: "/beheer/roosters/",
  })
  const router = useRouter()
  const navigate = useNavigate({ from: "/beheer/roosters/" })

  const { filterItems, activeFilters, queryFn } = useBaseScheduleFilters({
    onPageReset: () =>
      navigate({ search: (prev) => ({ ...prev, page: 1 }), replace: true }),
    finalized,
  })

  const {
    isFetching,
    data,
    search,
    setSearch,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    pagination,
    columnVisibility,
    setColumnVisibility,
    mergedFilters,
    selectedColumns,
    rowSelection,
    setRowSelection,
    selectedIds,
  } = usePaginatedTableWithUrlState({
    queryKey: [baseScheduleQueryKeys.all, activeFilters],
    queryFn,
    pageSize: TABLE_PAGE_SIZE,
    searchParams,
    navigate,
    allColumnIds: ALL_COLUMN_IDS,
    storageKey: BASE_SCHEDULE_STORAGE_KEY,
  })

  const schedules = data?.data.schedules || []

  const columns: ColumnDef<BaseSchedule>[] = [
    selectColumn(),
    createSortableColumn({ accessorKey: "id", name: "ID" }),
    createCustomColumn({
      id: "group",
      accessorKey: "group",
      name: "Groepnaam",
      cell: ({ row }) => row.original.lessonGroup.id,
    }),
    createCustomColumn({
      id: "teachersLabel",
      accessorKey: "teachersLabel",
      name: "Docent",
      cell: ({ row }) => row.original.teachersLabel,
    }),
    createSortableColumn({
      accessorKey: "numberOfLessonsRequired",
      name: "Aantal lessen",
    }),
    createCustomColumn({
      id: "status",
      accessorKey: "status",
      name: "Status",
      cell: ({ row }) => (
        <Badge variant={row.original.finalized ? "green" : "yellow"}>
          {row.original.finalized ? "Bevestigd" : "Concept"}
        </Badge>
      ),
    }),
  ]

  const { mutate: exportSchedules } = useExportMutation({
    exportApiPrefix: "schedules",
    filters: mergedFilters,
    selectedColumns,
    selectedIds,
    activeFilters,
  })

  const handleToggleStatus = (finalizedParam: boolean) => {
    navigate({
      search: (prev) => ({
        ...prev,
        finalized: finalizedParam,
      }),
    })
  }

  const handleNavigateToGroup = (groupId: Group["id"]) => {
    navigate({
      to: "/beheer/groepen/$groupId/rooster",
      params: { groupId: String(groupId) },
      search: { from: router.state.location.href },
    })
  }

  return (
    <ScreenWrapper showManagementMenu>
      <DataTable
        data={schedules}
        title="Roosters"
        searchPlaceholder="Filter roosters..."
        isLoading={isFetching}
        pagination={pagination}
        sortDirection={sortDirection}
        sortField={sortField}
        search={search}
        setSearch={setSearch}
        setSortField={setSortField}
        setSortDirection={setSortDirection}
        columnVisibility={columnVisibility}
        setColumnVisibility={setColumnVisibility}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        columns={columns}
        onRowClick={(_id, schedule) => {
          handleNavigateToGroup(schedule.lessonGroup.id)
        }}
        headerLeftActions={
          <>
            <MultiLevelDropdownMenu items={filterItems} />
            <Button
              variant={finalized === false ? "primary" : "ghost"}
              onClick={() => handleToggleStatus(false)}
            >
              Concept
            </Button>
            <Button
              variant={finalized === true ? "primary" : "ghost"}
              onClick={() => handleToggleStatus(true)}
            >
              Bevestigd
            </Button>
          </>
        }
        headerRightActions={
          <TableHeaderActions
            exportAction={{
              onClick: exportSchedules,
            }}
          />
        }
      />
    </ScreenWrapper>
  )
}
